/**
 * SOLID Review
 * S — Strong. One concern: name the browser's concretes the frontend graph is assembled from.
 * O — Good. A new host resource is one field.
 * L — N/A.
 * I — Strong. One function.
 * D — Good. The composition root: it may know the concretes — the Apollo client, the REST client, the
 *     stores, the websocket monitor, the CMP — and it is the only place that does.
 */
import { createClient } from 'graphql-ws';
import { WsConnectionMonitor, type IConfigDepWsConnectionMonitor } from '../../infrastructure/graphql/WsConnectionMonitor';
import { ApolloClientFactory, type IConfigDepApolloClient, type IConfigHandlerConnectionBanner } from '../../infrastructure/graphql/ApolloClientFactory';
import { BrowserWindowEvents } from '../../infrastructure/platform/BrowserWindowEvents';
import type { IServiceClock } from '../../infrastructure/platform/IServiceClock';
import type { IServiceTimers } from '../../infrastructure/platform/IServiceTimers';
import { BrowserConsentPage } from '../../infrastructure/consent/BrowserConsentPage';
import { ApolloDomainClient, RestApiClient, BeaconClient, type IConfigDepBeaconClient } from '../../infrastructure/contract/clients';
import type { IServicePageFacts } from '../../infrastructure/diagnostics/ClientDiagnosticsServerAdapter';
import type { ClientErrorReporting } from './ClientErrorReporting';
import type { IServiceLogger } from '../../infrastructure/platform/IServiceLogger';
import { API_BASE } from '../../infrastructure/api/apiBase';
import { ClientStores } from '../../infrastructure/state/ClientStores';
import { LiveKitClientAdapterFactory } from '../../infrastructure/livekit/liveKitClientAdapterFactory';
import type { IServiceLiveKitAdapter } from '../../application/ports/IGatewayLiveKit';
import { ConsentGateway } from '../../infrastructure/consent/ConsentGateway';
import { ManualConsentStore } from '../../infrastructure/consent/ManualConsentStore';
import { BrowserStorageArea } from '../../infrastructure/storage/BrowserStorageArea';
import type { IConfigDepBrowserStorages } from '../../infrastructure/storage/IServiceBrowserStorage';
import { SessionTokenStore } from '../../infrastructure/storage/SessionTokenStore';
import { GuestIdentityStore } from '../../infrastructure/storage/GuestIdentityStore';
import { tabPersistentStorage } from '../../shared/tabPersistentStorage';
import { readEnv } from '../../shared/viteEnv';
import { resolveHealthUrl } from '../../shared/appConfig';
import { PortObserverConsole } from './PortObserverConsole';
import type { IConfigDepFrontendPlatform } from './FrontendAssembly';

/**
 * THE BROWSER, AS THE FRONTEND GRAPH SEES IT (frontend strict-ports plan, FE1d, 2026-10-03) — the twin
 * of the backend's `HostPlatform`. One Apollo client for the six GraphQL wire sockets, one REST client
 * for the two REST ones, the stores, the websocket's state and the consent platform (FE4b, from App.tsx)
 * for Infrastructure, the console for the observer and for a half-wired graph.
 */
/**
 * The page's facts for an error report (client error reporting, 2026-10-10): its path, its build — the entry
 * module's file name, `index-Bx7f9aZk` in a production build — and its origin. Read defensively: the port and
 * UML generators assemble this graph under Node, where there is no document.
 */
export function pageFacts(doc: Pick<Document, 'querySelector'> | null, location: Pick<Location, 'pathname' | 'origin'>): IServicePageFacts {
  const src = doc?.querySelector<HTMLScriptElement>('script[type="module"][src]')?.getAttribute('src') ?? '';
  const entry = src.slice(src.lastIndexOf('/') + 1).replace(/\.[A-Za-z]+$/, '').replace(/[^A-Za-z0-9._-]/g, '').slice(0, 40);
  const facts: IServicePageFacts = { path: () => location.pathname, appVersion: entry === '' ? 'unknown' : entry, origin: location.origin };
  return facts;
}

/** The browser's two ways out for the beacon; `sendBeacon` is absent in some in-app browsers and under Node. */
export function beaconBrowser(nav: Partial<Pick<Navigator, 'sendBeacon'>> | null, send: typeof fetch): IConfigDepBeaconClient {
  const browser: IConfigDepBeaconClient = {
    sendBeacon: nav !== null && typeof nav.sendBeacon === 'function' ? (url, data) => nav.sendBeacon!(url, data) : null,
    fetch: (url, init) => send(url, init),
  };
  return browser;
}

/**
 * A frontend platform, and what the Framework's host takes from it: the websocket monitor whose state it reads (frontend-infrastructure-oo
 * P2) and the lazy LiveKit client factory (P4).
 */
export interface IEntityBrowserPlatform extends IConfigDepFrontendPlatform {
  readonly monitor: WsConnectionMonitor;
  readonly liveKit: IServiceLiveKitAdapter;
}

/** The server-busy hint, until the root hands the UI's: shown nowhere (the port and UML generators). */
const NO_BANNER: IConfigHandlerConnectionBanner = { onSlowStart: () => {}, onSlowEnd: () => {} };

/**
 * The browser's platform, built once (frontend-infrastructure-oo P2, §28.287): the environment and the page's location read here,
 * the clock, the timers, the storage, the page's events and the consent platform's page handed to the objects built over them —
 * the websocket monitor and the Apollo client among them, which built themselves on import until P2. The logger is the console's
 * (P3, §28.288): Infrastructure names no `console` of its own. The client's two stores are built here too (P4, §28.289), and only
 * the component's `ClientStateStore` is handed them — they were module singletons any module could import and write — as are the
 * REST client's `fetch` and the LiveKit client's timers, the last of the browser's resources the layer took from the global scope.
 */
export function browserPlatform(reporting: ClientErrorReporting | null = null, banner: IConfigHandlerConnectionBanner = NO_BANNER): IEntityBrowserPlatform {
  /* c8 ignore next -- the `{}` arm needs a loader with no import.meta.env; unreachable under Vite. */
  const env = (import.meta as unknown as { env?: Record<string, unknown> }).env ?? {};
  const clock: IServiceClock = { now: () => Date.now() };
  const timers: IServiceTimers = { setTimeout: (callback, ms) => setTimeout(callback, ms), clearTimeout: (timer) => clearTimeout(timer) };
  const logger: IServiceLogger = { warn: (message, ...details) => console.warn(message, ...details) };
  // The browser's storage areas, each resolved when it is used — reading `window.localStorage` throws where storage is
  // disabled, and the port and UML generators assemble this graph under Node, with no window (frontend-infrastructure-oo P1).
  const storages: IConfigDepBrowserStorages = {
    tab: tabPersistentStorage,
    session: new BrowserStorageArea(() => window.sessionStorage),
    local: new BrowserStorageArea(() => window.localStorage),
  };
  const tokens = new SessionTokenStore(storages, clock);
  const stores = new ClientStores(clock);
  const monitorDeps: IConfigDepWsConnectionMonitor = {
    url: WsConnectionMonitor.urlFor(env, window.location),
    tokens,
    page: new BrowserWindowEvents(() => window, () => document),
    timers,
    clock,
    createClient,
  };
  const monitor = new WsConnectionMonitor(monitorDeps);
  const apolloDeps: IConfigDepApolloClient = {
    httpUrl: ApolloClientFactory.httpUrlFor(env, window.location),
    fetch: (input, init) => fetch(input, init),
    tokens,
    ws: monitor.client,
    banner,
    timers,
  };
  const apolloClient = new ApolloClientFactory(apolloDeps).create();
  const platform: IEntityBrowserPlatform = {
    clients: {
      graphql: new ApolloDomainClient(apolloClient),
      rest: new RestApiClient(API_BASE, (url, init) => fetch(url, init)),
      /* c8 ignore next -- the `null` arm is the port and UML generators', which assemble this graph under Node. */
      beacon: new BeaconClient(API_BASE, beaconBrowser(typeof navigator === 'undefined' ? null : navigator, (url, init) => fetch(url, init))),
    },
    host: {
      tableStore: stores.table,
      sessionStore: stores.session,
      tokens,
      guests: new GuestIdentityStore(storages),
      connection: monitor,
      // Build-time config: unset (dev, or a deployment without a Funding Choices account), the gateway
      // falls back to the opt-out default after a short timeout, so the app stays usable. Read through
      // `readEnv`: the port and UML generators assemble this graph under Node, without Vite.
      consentGateway: new ConsentGateway(
        new ManualConsentStore(storages.local, clock), new BrowserConsentPage(() => window), timers, readEnv('VITE_GOOGLE_FC_PUBLISHER_ID') ?? null,
      ),
      healthUrl: resolveHealthUrl(env, window.location),
      /* c8 ignore next -- likewise: under Node there is no document. */
      page: pageFacts(typeof document === 'undefined' ? null : document, window.location),
      fetch: (url, init) => fetch(url, init),
      logger,
      clock,
    },
    observer: reporting === null ? new PortObserverConsole() : reporting.observer(new PortObserverConsole()),
    report: (message) => console.error('[FrontendAssembly]', message),
    monitor,
    liveKit: new LiveKitClientAdapterFactory(logger, timers),
  };
  return platform;
}
